import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import * as moment from "moment";
import { Link } from "react-router-dom";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import TableContainer from "../../../Components/Common/TableContainer";
import DeleteModal from "../../../Components/Common/DeleteModal";
import { isEmpty } from "lodash";

// Formik
import * as Yup from "yup";
import { useFormik } from "formik";

//redux
import { useSelector, useDispatch } from "react-redux";

import { getPhases as onGetPhases } from "../../../slices/thunks";

import {
  getBlocks as onGetBlocks,
  addNewBlock as onAddNewBlock,
  updateBlock as onUpdateBlock,
  deleteBlock as onDeleteBlock,
} from "../../../slices/thunks";

import Loader from "../../../Components/Common/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ExportCSVModal from "../../../Components/Common/ExportCSVModal";
import { createSelector } from "reselect";
import { useProfile } from "../../../Components/Hooks/UserHooks";

const BlockManagement = () => {
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [phases, setPhases] = useState([]);

  const dispatch = useDispatch();

  const selectLayoutState = (state) => state.Buildings;
  const selectLayoutProperties = createSelector(selectLayoutState, (build) => ({
    blocks: build.blocks,
    isBlockSuccess: build.isBlockSuccess,
    error: build.error,
  }));
  // Inside your component
  const { blocks, isBlockSuccess, error } = useSelector(selectLayoutProperties);

  const { userProfile } = useProfile();

  const [pageSize, setPageSize] = useState(10);
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 480) {
        setPageSize(5); // Small screen (mobile) should have 5 items per page
      } else {
        setPageSize(10); // Larger screen (tablet/desktop) should have 10 items per page
      }
    };

    // Run on initial load
    handleResize();

    // Attach resize event listener
    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isAdmin = userProfile?.user?.groups?.includes("Admin");

  useEffect(() => {
    dispatch(onGetPhases()).then((response) => {
      setPhases(response.payload || []);
    });
  }, [dispatch]);

  const [blockList, setBlockList] = useState([]);
  const [block, setBlock] = useState([]);

  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);

  const onClickDelete = (block) => {
    setBlock(block);
    setDeleteModal(true);
  };

  const handleDeleteBlock = () => {
    if (block) {
      dispatch(onDeleteBlock({ blockId: block.id }));
      setDeleteModal(false);
    }
  };

  useEffect(() => {
    setBlockList(blocks);
  }, [blocks]);

  useEffect(() => {
    if (!isEmpty(blocks)) setBlockList(blocks);
  }, [blocks]);

  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      let filteredBlocks = blocks;
      if (type !== "all") {
        filteredBlocks = blocks.filter((block) => block.status === type);
      }
      setBlockList(filteredBlocks);
    }
  };

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      id: (block && block.id) || "",
      phase: (block && block.phase) || null,
      name: (block && block.name) || "",
      description: (block && block.description) || null,
      total_flats: (block && block.total_flats) || null,
      total_floors: (block && block.total_floors) || null,
      total_terrace_surface: (block && block.total_terrace_surface) || null,
      total_apartment_surface: (block && block.total_apartment_surface) || null,
      created_at: (block && block.created_at) || "",
      active: (block && block.active) || true,
    },
    validationSchema: Yup.object({
      phase: Yup.string().required("Please select a Phase"),
      name: Yup.string()
        .required("Please Enter Name")
        .min(3, "Name must be at least 3 characters"),
      description: Yup.string().required("Please Enter Description"),
      total_flats: Yup.number().required("Please Enter Total Flats"),
      total_floors: Yup.number().required("Please Enter Total Floors"),
      total_terrace_surface: Yup.number().required(
        "Please Enter Total Terrace Surface"
      ),
      total_apartment_surface: Yup.number().required(
        "Please Enter Total Apartment Surface"
      ),
      active: Yup.boolean().required("Please specify if active"),
    }),
    onSubmit: (values) => {
      if (isEdit) {
        const updatedBlock = {
          id: block ? block.id : 0,
          phase: values.phase,
          name: values.name,
          description: values.description,
          total_flats: values.total_flats,
          total_floors: values.total_floors,
          total_terrace_surface: values.total_terrace_surface,
          total_apartment_surface: values.total_apartment_surface,
          active: values.active,
        };
        dispatch(onUpdateBlock({ block: updatedBlock }));
        validation.resetForm();
      } else {
        const newBlock = {
          phase: values.phase,
          name: values.name,
          description: values.description,
          total_flats: values.total_flats,
          total_floors: values.total_floors,
          total_terrace_surface: values.total_terrace_surface,
          total_apartment_surface: values.total_apartment_surface,
          active: values.active,
        };
        dispatch(onAddNewBlock({ block: newBlock }));
        validation.resetForm();
      }
      toggle();
    },
  });

  useEffect(() => {
    dispatch(onGetBlocks());
  }, [dispatch]);

  useEffect(() => {
    setBlock(blocks);
  }, [blocks]);

  useEffect(() => {
    if (!isEmpty(blocks)) {
      setBlock(blocks);
      setIsEdit(false);
    }
  }, [blocks]);

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setBlock(null);
    } else {
      setModal(true);
      setDate(defaultdate());
    }
  }, [modal]);

  const handleOrderClicks = () => {
    setBlock("");
    setIsEdit(false);
    toggle();
  };

  const handleBlockClick = useCallback(
    (arg) => {
      const block = arg;

      const associatedPhase = phases.find((phase) => phase.id === block.phase);

      if (!associatedPhase) {
        toast.error("Phase data is not loaded yet. Please try again later.");
        return;
      }

      setBlock({
        id: block.id,
        phase: block.phase,
        phaseName: associatedPhase.name,
        name: block.name,
        description: block.description,
        total_flats: block.total_flats,
        total_floors: block.total_floors,
        total_terrace_surface: block.total_terrace_surface,
        total_apartment_surface: block.total_apartment_surface,
        created_at: block.created_at,
        active: block.active,
      });

      setIsEdit(true);
      toggle();
    },
    [toggle, phases]
  );

  const checkedAll = useCallback(() => {
    const checkall = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".orderCheckBox");
    if (checkall.checked) {
      ele.forEach((ele) => {
        ele.checked = true;
      });
    } else {
      ele.forEach((ele) => {
        ele.checked = false;
      });
    }
    deleteCheckbox();
  }, []);
  // Delete Multiple
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);

  const deleteMultiple = () => {
    const checkall = document.getElementById("checkBoxAll");
    selectedCheckBoxDelete.forEach((element) => {
      dispatch(onDeleteBlock(element.value));
      setTimeout(() => {
        toast.clearWaitingQueue();
      }, 3000);
    });
    setIsMultiDeleteButton(false);
    checkall.checked = false;
  };

  const deleteCheckbox = () => {
    const ele = document.querySelectorAll(".orderCheckBox:checked");
    ele.length > 0
      ? setIsMultiDeleteButton(true)
      : setIsMultiDeleteButton(false);
    setSelectedCheckBoxDelete(ele);
  };

  // Column
  const columns = useMemo(
    () => [
      {
        header: "Phases",
        accessorKey: "phase",
        enableColumnFilter: false,
        cell: (cell) => {
          const phaseId = cell.getValue();
          const phase = phases.find((phas) => phas.id === phaseId);
          return phase ? phase.name : <></>;
        },
      },
      {
        header: "Name",
        accessorKey: "name",
        enableColumnFilter: false,
        // cell: (cell) => {
        //   return <Link to="/apps-ecommerce-block-details" className="fw-medium link-primary">{cell.getValue()}</Link>;
        // },
      },
      {
        header: "Description",
        accessorKey: "description",
        enableColumnFilter: false,
      },
      {
        header: "Total Flats",
        accessorKey: "total_flats",
        enableColumnFilter: false,
      },
      {
        header: "Total Floors",
        accessorKey: "total_floors",
        enableColumnFilter: false,
      },
      {
        header: "Total Terrace Surface",
        accessorKey: "total_terrace_surface",
        enableColumnFilter: false,
      },
      {
        header: "Total Apartment Surface",
        accessorKey: "total_apartment_surface",
        enableColumnFilter: false,
      },
      // {
      //   header: "Created At",
      //   accessorKey: "created_at",
      //   enableColumnFilter: false,
      //   cell: (cell) => (
      //     <>
      //       {handleValidDate(cell.getValue())},
      //       <small className="text-muted"> {handleValidTime(cell.getValue())}</small>
      //     </>
      //   ),
      // },
      {
        header: "Active",
        accessorKey: "active",
        enableColumnFilter: false,
      },
      {
        header: "Action",
        cell: (cellProps) => {
          const isAdmin = userProfile?.user?.groups?.includes("Admin");

          return (
            <ul className="list-inline hstack gap-2 mb-0">
              <li className="list-inline-item">
                <Link
                  to={`/block-details/${cellProps.row.original.id}`}
                  className="text-primary d-inline-block"
                >
                  <i className="ri-eye-fill fs-16"></i>
                </Link>
              </li>
              {isAdmin && (
                <>
                  <li className="list-inline-item edit">
                    <Link
                      to="#"
                      className="text-primary d-inline-block edit-item-btn"
                      onClick={() => {
                        const blockData = cellProps.row.original;
                        handleBlockClick(blockData);
                      }}
                    >
                      <i className="ri-pencil-fill fs-16"></i>
                    </Link>
                  </li>
                  <li className="list-inline-item">
                    <Link
                      to="#"
                      className="text-danger d-inline-block remove-item-btn"
                      onClick={() => {
                        const blockData = cellProps.row.original;
                        onClickDelete(blockData);
                      }}
                    >
                      <i className="ri-delete-bin-5-fill fs-16"></i>
                    </Link>
                  </li>
                </>
              )}
            </ul>
          );
        },
      },
    ],
    [handleBlockClick, checkedAll]
  );

  const defaultdate = () => {
    let d = new Date(),
      months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
    let h = d.getHours() % 12 || 12;
    let ampm = d.getHours() < 12 ? "AM" : "PM";
    return (
      d.getDate() +
      " " +
      months[d.getMonth()] +
      ", " +
      d.getFullYear() +
      ", " +
      h +
      ":" +
      d.getMinutes() +
      " " +
      ampm
    ).toString();
  };

  const [date, setDate] = useState(defaultdate());
  const dateformate = (e) => {
    const dateString = e.toString().split(" ");

    let time = dateString[4];
    let H = +time.substr(0, 2);
    let h = H % 12 || 12;
    h = h <= 9 ? (h = "0" + h) : h;
    let ampm = H < 12 ? "AM" : "PM";
    time = h + time.substr(2, 3) + " " + ampm;

    const date = dateString[2] + " " + dateString[1] + ", " + dateString[3];
    const orderDate = (date + ", " + time).toString();
    setDate(orderDate);
  };

  const handleValidDate = (date) => {
    const date1 = moment(new Date(date)).format("DD MMM Y");
    return date1;
  };

  const handleValidTime = (time) => {
    const time1 = new Date(time);
    const getHour = time1.getUTCHours();
    const getMin = time1.getUTCMinutes();
    const getTime = `${getHour}:${getMin}`;
    var meridiem = "";
    if (getHour >= 12) {
      meridiem = "PM";
    } else {
      meridiem = "AM";
    }
    const updateTime =
      moment(getTime, "hh:mm").format("hh:mm") + " " + meridiem;
    return updateTime;
  };

  const [isExportCSV, setIsExportCSV] = useState(false);

  return (
    <div className="page-content">
      <ExportCSVModal
        show={isExportCSV}
        onCloseClick={() => setIsExportCSV(false)}
        data={blockList}
      />

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeleteBlock}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <Container fluid>
        {/* <BreadCrumb title="Block" pageTitle="Tables" /> */}
        <Row>
          <Col lg={12}>
            <Card id="orderList">
              <CardHeader className="border-0">
                <Row className="align-items-center gy-3 h-25">
                  <div className="col-sm">
                    {/* <h5 className="card-title mb-0">Block History</h5> */}
                  </div>
                  <div className="col-sm-auto" style={{ marginBottom: "10px" }}>
                    <div className="d-flex gap-1 flex-wrap">
                      {isAdmin && (
                        <button
                          type="button"
                          className="btn btn-success add-btn"
                          id="create-btn"
                          onClick={() => {
                            setIsEdit(false);
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>{" "}
                          Create Block
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={() => setIsExportCSV(true)}
                      >
                        <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                        Export
                      </button>{" "}
                      {isMultiDeleteButton && (
                        <button
                          className="btn btn-soft-danger"
                          onClick={() => setDeleteModalMulti(true)}
                        >
                          <i className="ri-delete-bin-2-line"></i>
                        </button>
                      )}
                    </div>
                  </div>
                </Row>
              </CardHeader>

              <CardBody className="pt-0">
                <div>
                  {/* <Nav
                    className="nav-tabs nav-tabs-custom nav-success"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "1" })}
                        onClick={() => {
                          toggleTab("1", "all");
                        }}
                        href="#"
                      >
                        <i className="ri-store-2-fill me-1 align-bottom"></i>{" "}
                        Blocks
                      </NavLink>
                    </NavItem>
                  </Nav> */}
                  {isBlockSuccess ? (
                    phases.length === 0 ? (
                      <div className="text-center mt-5">
                        <p className="text-danger">
                          No phases available. Please add a phase before adding
                          blocks.
                        </p>
                      </div>
                    ) : blockList.length > 0 ? (
                      <TableContainer
                        columns={columns}
                        data={blockList || []}
                        // isGlobalFilter={true}
                        isAddUserList={false}
                        customPageSize={pageSize}
                        divClass="table-responsive table-card mb-1"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted text-uppercase"
                        handleOrderClick={handleOrderClicks}
                        // isOrderFilter={true}
                        SearchPlaceholder="Search for Id, description or something..."
                      />
                    ) : (
                      <div className="text-center mt-5">
                        <p className="text-muted">
                          No blocks are introduced yet. Please add a new block.
                        </p>
                      </div>
                    )
                  ) : (
                    <Loader error={error} />
                  )}
                </div>
                <Modal id="showModal" isOpen={modal} toggle={toggle} centered>
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {!!isEdit ? "Edit Block" : "Add Block"}
                  </ModalHeader>

                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                  >
                    <ModalBody>
                      <input type="hidden" id="id-field" />
                      <div className="mb-3">
                        <Label htmlFor="phase-field" className="form-label">
                          Phase
                        </Label>
                        <Input
                          type="select"
                          name="phase"
                          id="phase-field"
                          className="form-select"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.phase || ""}
                          invalid={
                            validation.touched.phase && validation.errors.phase
                              ? true
                              : false
                          }
                        >
                          <option value="">Select a phase</option>
                          {phases.map((proj) => (
                            <option key={proj.id} value={proj.id}>
                              {proj.name}
                            </option>
                          ))}
                        </Input>
                        {validation.touched.phase &&
                          validation.errors.phase && (
                            <FormFeedback type="invalid">
                              {validation.errors.phase}
                            </FormFeedback>
                          )}
                      </div>

                      <div className="mb-3">
                        <Label htmlFor="id-field" className="form-label">
                          Name
                        </Label>
                        <Input
                          name="name"
                          id="id-field"
                          className="form-control"
                          placeholder="Enter Name"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.name || ""}
                          invalid={
                            validation.touched.name && validation.errors.name
                              ? true
                              : false
                          }
                        />
                        {validation.touched.name && validation.errors.name ? (
                          <FormFeedback type="invalid">
                            {validation.errors.name}
                          </FormFeedback>
                        ) : null}
                      </div>

                      <div className="mb-3">
                        <Label
                          htmlFor="customername-field"
                          className="form-label"
                        >
                          Description
                        </Label>
                        <Input
                          name="description"
                          id="customername-field"
                          className="form-control"
                          placeholder="Enter Description"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.description || ""}
                          invalid={
                            validation.touched.description &&
                            validation.errors.description
                              ? true
                              : false
                          }
                        />
                        {validation.touched.description &&
                        validation.errors.description ? (
                          <FormFeedback type="invalid">
                            {validation.errors.description}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="customername-field"
                          className="form-label"
                        >
                          Total Flats
                        </Label>
                        <Input
                          name="total_flats"
                          id="customername-field"
                          className="form-control"
                          placeholder="Enter Total Flats"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.total_flats || ""}
                          invalid={
                            validation.touched.total_flats &&
                            validation.errors.total_flats
                              ? true
                              : false
                          }
                        />
                        {validation.touched.total_flats &&
                        validation.errors.total_flats ? (
                          <FormFeedback type="invalid">
                            {validation.errors.total_flats}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="customername-field"
                          className="form-label"
                        >
                          Total Floors
                        </Label>
                        <Input
                          name="total_floors"
                          id="customername-field"
                          className="form-control"
                          placeholder="Enter total Floors"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.total_floors || ""}
                          invalid={
                            validation.touched.total_floors &&
                            validation.errors.total_floors
                              ? true
                              : false
                          }
                        />
                        {validation.touched.total_floors &&
                        validation.errors.total_floors ? (
                          <FormFeedback type="invalid">
                            {validation.errors.total_floors}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="customername-field"
                          className="form-label"
                        >
                          Total Terrace Surface
                        </Label>
                        <Input
                          name="total_terrace_surface"
                          id="customername-field"
                          className="form-control"
                          placeholder="Enter Terrace Surface"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.total_terrace_surface || ""}
                          invalid={
                            validation.touched.total_terrace_surface &&
                            validation.errors.total_terrace_surface
                              ? true
                              : false
                          }
                        />
                        {validation.touched.total_terrace_surface &&
                        validation.errors.total_terrace_surface ? (
                          <FormFeedback type="invalid">
                            {validation.errors.total_terrace_surface}
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mb-3">
                        <Label
                          htmlFor="customername-field"
                          className="form-label"
                        >
                          Total Apartment Surface
                        </Label>
                        <Input
                          name="total_apartment_surface"
                          id="customername-field"
                          className="form-control"
                          placeholder="Enter Apartment Surface"
                          type="text"
                          validate={{
                            required: { value: true },
                          }}
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={
                            validation.values.total_apartment_surface || ""
                          }
                          invalid={
                            validation.touched.total_apartment_surface &&
                            validation.errors.total_apartment_surface
                              ? true
                              : false
                          }
                        />
                        {validation.touched.total_apartment_surface &&
                        validation.errors.total_apartment_surface ? (
                          <FormFeedback type="invalid">
                            {validation.errors.total_apartment_surface}
                          </FormFeedback>
                        ) : null}
                      </div>
                      
                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <button
                          type="button"
                          className="btn btn-light"
                          onClick={() => {
                            setModal(false);
                          }}
                        >
                          Close
                        </button>

                        <button type="submit" className="btn btn-success">
                          {!!isEdit ? "Update" : "Add Block"}
                        </button>
                      </div>
                    </div>
                  </Form>
                </Modal>
                <ToastContainer closeButton={false} limit={1} />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlockManagement;
